/**
 * readOfDay — the overview hero's "read of the day".
 *
 * The one editorial line under the hero number. A ranked-rule picker that
 * surfaces the most *interesting true* thing about the region's rhythm right
 * now — deliberately carrying the
 * dimension the trend pill CANNOT: time. The pill already says "▲ +12% sul
 * tipico"; this line says *when the peak usually lands*, *whether we're past
 * it*, *how this compares to a typical <weekday>*. That "compared to when?"
 * framing is the product thesis — we gave the queue a memory (DESIGN.md).
 *
 * Design contract (DESIGN.md → "honesty first", "the bar: the last 10%"):
 *  - Never merely restate the pill; add the rhythm/time story instead.
 *  - On a weak/absent signal, fall back to a plain true line — never fabricate.
 *  - Exactly ONE token per line is accented (rendered in the brand rose), the
 *    way the h1 italicises "Alto Adige". The accent is ALWAYS the brand hue,
 *    never severity-coloured — triage stays quarantined, we never cry wolf.
 *
 * Deterministic + testable: peak-position reasoning uses the real Europe/Rome
 * clock (`now`), falling back to the last datapoint's hour when it's absent;
 * direction comes from `regionTrend`. Pass a fixed `now` in tests to pin output.
 * Copy is authored as ordered segments (a slotted-string shape) so the accented
 * token is *data*, not markup — and so DE/EN can slot in later without rework.
 */

import type { RegionTrendResponse } from "@/api/generated/model/regionTrendResponse";
import type { RegionTypicalPoint } from "@/api/generated/model/regionTypicalPoint";
import { regionTrend } from "@/lib/regionTrend";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ReadSegment {
  text: string;
  /** When true, render this token in the brand pulse hue (the one emphasis). */
  accent?: boolean;
}

/** Stable id of the rule that fired — for tests/analytics; never shown. */
export type ReadKey =
  | "no-data"
  | "trough"
  | "busy-before-peak"
  | "busy-near-peak"
  | "busy-after-peak"
  | "busy"
  | "calm"
  | "norm-before-peak"
  | "norm";

export interface ReadOfDay {
  key: ReadKey;
  /** Ordered segments; accent segments render in the brand hue. */
  segments: ReadSegment[];
  /** Flattened text — accessible name / fallback / test convenience. */
  plain: string;
}

// ── Segment helpers ──────────────────────────────────────────────────────────────

const t = (text: string): ReadSegment => ({ text });
const a = (text: string): ReadSegment => ({ text, accent: true });

function build(key: ReadKey, segments: ReadSegment[]): ReadOfDay {
  return { key, segments, plain: segments.map((s) => s.text).join("") };
}

// ── Rhythm derivations ───────────────────────────────────────────────────────────

interface Extreme {
  hourLocal: number;
  avg: number;
}

/**
 * The typical curve's peak and trough hours — but only when they're
 * *meaningfully* above/below the day's mean. A flat curve has no real peak, and
 * "the peak is at 14:00" on a flat day is noise, not insight. Requires ≥3
 * well-sampled slots so a thin history doesn't invent a rhythm.
 */
function typicalExtremes(typical: readonly RegionTypicalPoint[]): {
  peak?: Extreme;
  trough?: Extreme;
} {
  const pts: Extreme[] = [];
  for (const p of typical) {
    if (p.avg !== null && p.sampleCount > 0) pts.push({ hourLocal: p.hourLocal, avg: p.avg });
  }
  if (pts.length < 3) return {};

  let peak = pts[0]!;
  let trough = pts[0]!;
  let sum = 0;
  for (const p of pts) {
    if (p.avg > peak.avg) peak = p;
    if (p.avg < trough.avg) trough = p;
    sum += p.avg;
  }
  const mean = sum / pts.length;

  const out: { peak?: Extreme; trough?: Extreme } = {};
  if (peak.avg >= mean * 1.15) out.peak = peak;
  if (trough.avg <= mean * 0.85) out.trough = trough;
  return out;
}

/**
 * Weekdays carry grammatical gender: six are masculine, but *domenica* (Sunday)
 * is feminine, so the article and any agreeing adjective must inflect — else the
 * hero reads "un domenica tipico" one day in seven. Monday-first (index 0 = ISO 1).
 */
interface Weekday {
  name: string;
  fem: boolean;
}
const WEEKDAYS_IT: Weekday[] = [
  { name: "lunedì", fem: false },
  { name: "martedì", fem: false },
  { name: "mercoledì", fem: false },
  { name: "giovedì", fem: false },
  { name: "venerdì", fem: false },
  { name: "sabato", fem: false },
  { name: "domenica", fem: true },
];

function weekday(dayOfWeek: number): Weekday {
  return WEEKDAYS_IT[dayOfWeek - 1] ?? { name: "giorno", fem: false };
}

/** Indefinite article agreeing with gender; `capital` for sentence-start use. */
function indefArticle(fem: boolean, capital = false): string {
  const art = fem ? "una" : "un";
  return capital ? art.charAt(0).toUpperCase() + art.slice(1) : art;
}

/** The current hour-of-day in Europe/Rome (0–23) from a real clock instant. */
function romeHour(now: Date): number {
  const s = new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    hour12: false,
    timeZone: "Europe/Rome",
  }).format(now);
  return Number.parseInt(s, 10) % 24;
}

/** The time token, accented on its own — "le 11", with the "l'1" special case. */
function hourLabel(h: number): string {
  return h === 1 ? "l'1" : `le ${h}`;
}

// ── The picker ───────────────────────────────────────────────────────────────────

const NO_DATA = (): ReadOfDay =>
  build("no-data", [t("I dati "), a("in tempo reale"), t(" si aggiornano ogni pochi minuti.")]);

/**
 * Choose the single most interesting true line for the region right now.
 * Priority order (first true wins): no-data → quiet-trough → busy(by peak
 * position) → calm → normal(with peak hook) → plain normal.
 *
 * `now` (the real clock instant) drives the peak-position reasoning — the "are
 * we before/after the peak?" question is about the time *of day*, which the
 * wall clock knows even when the feed is stale. Using the last datapoint's hour
 * instead would let a stale feed assert a false forward-looking claim ("il picco
 * arriva verso le 11" at 15:00). When `now` is absent we fall back to the last
 * datapoint's hour. Direction (busy/calm) still comes from the data via
 * `regionTrend`.
 */
export function readOfDay(trend: RegionTrendResponse | undefined, now?: Date): ReadOfDay {
  if (!trend) return NO_DATA();

  const { direction } = regionTrend(trend.today, trend.typical);
  if (direction === "noData") return NO_DATA();

  const lastToday = [...trend.today].reverse().find((p) => p.waiting !== null);
  const currentHour = now ? romeHour(now) : lastToday ? lastToday.hourLocal : 0;

  const { peak, trough } = typicalExtremes(trend.typical);
  const day = weekday(trend.dayOfWeek);

  const beforePeak = peak !== undefined && currentHour <= peak.hourLocal - 2;
  const nearPeak = peak !== undefined && Math.abs(currentHour - peak.hourLocal) <= 1;
  const afterPeak = peak !== undefined && currentHour >= peak.hourLocal + 2;
  const nearTrough = trough !== undefined && Math.abs(currentHour - trough.hourLocal) <= 1;

  // 1) Quiet trough — a small delight at the calmest hour. Suppressed when
  //    we're actually busier than usual (that would contradict "quietest").
  if (nearTrough && direction !== "up") {
    return build("trough", [t("Siamo nell'"), a("ora più quieta"), t(" della giornata.")]);
  }

  // 2) Busier than typical — lead with the time story, not a louder "+%".
  if (direction === "up") {
    if (beforePeak && peak) {
      return build("busy-before-peak", [
        t("Già più affollato del solito, e il picco di norma arriva verso "),
        a(hourLabel(peak.hourLocal)),
        t("."),
      ]);
    }
    if (nearPeak) {
      return build("busy-near-peak", [
        t("Più affollato del solito: è l'"),
        a("ora di punta"),
        t(` per ${indefArticle(day.fem)} ${day.name}.`),
      ]);
    }
    if (afterPeak) {
      return build("busy-after-peak", [
        t("Ancora affollato, anche se "),
        a("il picco è ormai passato"),
        t("."),
      ]);
    }
    return build("busy", [t("Un po' "), a("più affollato"), t(" del solito per quest'ora.")]);
  }

  // 3) Calmer than typical. Adjectives agree with the weekday's gender
  //    ("tranquilla … una domenica tipica").
  if (direction === "down") {
    return build("calm", [
      t("Più "),
      a(day.fem ? "tranquilla" : "tranquillo"),
      t(` di ${indefArticle(day.fem)} ${day.name} ${day.fem ? "tipica" : "tipico"} a quest'ora.`),
    ]);
  }

  // 4) In the normal band — still add the rhythm hook when the peak is known.
  if (beforePeak && peak) {
    return build("norm-before-peak", [
      t("Nella media per l'ora; il picco di solito arriva verso "),
      a(hourLabel(peak.hourLocal)),
      t("."),
    ]);
  }
  return build("norm", [
    t(`${indefArticle(day.fem, true)} ${day.name} `),
    a("nella norma"),
    t(": nessuna sorpresa a quest'ora."),
  ]);
}
